import { useQuery } from "@tanstack/react-query";
import { UserRound } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAgentImpersonation } from "@/contexts/AgentImpersonationContext";
import { SelectorVerComo, type GrupoVerComo } from "@/components/ui/selector-ver-como";

// Roles impersonables desde el Portal Agente. Hardcode a propósito: aún no
// usamos una columna en BD. Incluye los roles con acceso real al portal
// (1 Super Admin, 2 Admin Proyecto, 3 Agente Inmob., 9 Agente Interno) + el
// rol 30 (Super Admin "fake" que existe en prod pero no en dev; se quiere ver).
const AGENT_PORTAL_ROLE_IDS = [1, 2, 3, 9, 30];

// Etiqueta corta por rol para la nota de cada opción.
const ROLE_BADGE: Record<number, string> = {
  1: "Super Admin",
  2: "Admin Proy.",
  3: "Agente Inmob.",
  9: "Interno",
  30: "Super Admin",
};

/**
 * Selector "Ver como" del header del Portal Agente, pintado con el estándar `SelectorVerComo`.
 * El modo de vista (completa / del usuario) va en la prop `modo`: el conmutador arriba de la
 * lista mientras se impersona, mismo lugar en todos los portales.
 */
export function AgentPortalImpersonationSelector() {
  const { profile } = useAuth();
  const {
    impersonatedAgentEmail,
    impersonatedAgentName,
    setImpersonatedAgent,
    clearImpersonation,
    isImpersonating,
  } = useAgentImpersonation();
  const canImpersonate = profile?.puede_impersonar === true;

  const roleIds = AGENT_PORTAL_ROLE_IDS;

  const { data: agents = [], isLoading } = useQuery({
    queryKey: ["all-agents-for-portal-impersonation", roleIds],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("usuarios")
        .select("email, rol_id, auth_user_id, personas(id, nombre_legal)")
        .in("rol_id", roleIds)
        .eq("activo", true)
        .order("email");

      if (error) throw error;
      return (data || [])
        .map((u: any) => ({
          email: u.email,
          rolId: u.rol_id,
          personaId: u.personas?.id ?? null,
          // Necesario para que las consultas que filtran por auth.uid() (prospectos,
          // atribución, notas) devuelvan la cartera del agente y no la del admin.
          authUserId: u.auth_user_id ?? null,
          nombre: u.personas?.nombre_legal || u.email,
          rolLabel: ROLE_BADGE[u.rol_id] ?? `Rol ${u.rol_id}`,
        }))
        .sort((a: any, b: any) => a.nombre.localeCompare(b.nombre));
    },
    enabled: canImpersonate,
  });

  if (!canImpersonate) return null;

  const etiqueta = isImpersonating
    ? impersonatedAgentName || impersonatedAgentEmail || "Agente"
    : "Mi vista";

  const grupos: GrupoVerComo[] = [];
  grupos.push({
    titulo: "Usuarios",
    opciones: agents.map((agent: any) => ({
      key: agent.email,
      label: agent.nombre,
      buscarPor: agent.email,
      nota: agent.rolLabel,
      seleccionada: impersonatedAgentEmail === agent.email,
      onSelect: () => setImpersonatedAgent(agent.email, agent.personaId, agent.nombre, agent.rolId, agent.authUserId),
    })),
  });

  return (
    <SelectorVerComo
      etiqueta={etiqueta}
      impersonando={isImpersonating}
      iconoVistaPropia={<UserRound className="size-3.5" strokeWidth={1.75} />}
      opcionPorDefecto={{
        key: "__mi_vista__",
        label: "Mi vista",
        nota: "Por defecto",
        seleccionada: !isImpersonating,
        onSelect: clearImpersonation,
        icono: <UserRound className="mr-2 size-3.5 shrink-0 text-primary" />,
      }}
      grupos={grupos}
      onLimpiar={clearImpersonation}
      ariaLimpiar="Dejar de impersonar"
      textoVacio="No se encontró el usuario."
      cargando={isLoading}
    />
  );
}
